import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../services/config';
  import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    FormHelperText,
    Alert,
    CircularProgress,
    IconButton,
    Stack,
  } from '@mui/material';
import {
  ArrowBack,
  Save,
  LocationOn,
  CalendarToday,
  AttachMoney,
  People,
  Add,
  Delete,
  Upload,
  Assignment,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useCompanySelector from '../hooks/useCompanySelector';

interface Milestone {
  id: string;
  name: string;
  targetDate: string;
  description: string;
}

interface ProjectDocument {
  id: string;
  name: string;
  file: File | null;
  description: string;
}

interface ProjectFormData {
  name: string;
  projectNumber: string;
  description: string;
  location: string;
  startDate: string;
  endDate: string;
  budget: string;
  status: string;
  priority: string;
  type: string;
  clientName: string;
  managerId: string;
  milestones: Milestone[];
  documents: ProjectDocument[];
}

const ProjectCreate: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const {
    isAdmin,
    companies,
    selectedCompanyId,
    setSelectedCompanyId,
  } = useCompanySelector();

  const [formData, setFormData] = useState<ProjectFormData>({
    name: '',
    projectNumber: '',
    description: '',
    location: '',
    startDate: '',
    endDate: '',
    budget: '',
    status: 'planning',
    priority: 'medium',
    type: '',
    clientName: '',
    managerId: user?._id || '',
    milestones: [],
    documents: [],
  });

  const [errors, setErrors] = useState<Partial<ProjectFormData>>({});

  const statusOptions = [
    { value: 'planning', label: 'Planning' },
    { value: 'active', label: 'Active' },
    { value: 'on_hold', label: 'On Hold' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' },
  ];

  const typeOptions = [
    { value: 'residential', label: 'Residential' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'industrial', label: 'Industrial' },
    { value: 'infrastructure', label: 'Infrastructure' },
    { value: 'renovation', label: 'Renovation' },
  ];

  const validateForm = (): boolean => {
    const newErrors: Partial<ProjectFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Project name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.projectNumber.trim()) {
      newErrors.projectNumber = 'Project number is required';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    } else if (formData.startDate && new Date(formData.endDate) <= new Date(formData.startDate)) {
      newErrors.endDate = 'End date must be after start date';
    }

    if (!formData.budget || parseFloat(formData.budget) <= 0) {
      newErrors.budget = 'Valid budget amount is required';
    }

    if (!formData.type) {
      newErrors.type = 'Project type is required';
    }

    if (!formData.clientName.trim()) {
      newErrors.clientName = 'Client name is required';
    }

    const failedFields = Object.keys(newErrors);
    if (failedFields.length > 0) {
      console.log('[ProjectCreate] Validation errors', {
        failedFields,
        errors: newErrors,
        formSnapshot: {
          name: formData.name,
          projectNumber: formData.projectNumber,
          description: formData.description,
          location: formData.location,
          startDate: formData.startDate,
          endDate: formData.endDate,
          budget: formData.budget,
          status: formData.status,
          priority: formData.priority,
          type: formData.type,
          clientName: formData.clientName,
          managerId: formData.managerId,
        },
      });
    }

    setErrors(newErrors);
    return failedFields.length === 0;
  };

  // Company selection is managed by useCompanySelector; no additional bootstrap needed

  const handleInputChange = (field: keyof ProjectFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: undefined,
      });
    }
  };

  const handleSelectChange = (field: keyof ProjectFormData) => (
    event: any
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    console.log('[ProjectCreate] Create Project submit clicked', { time: new Date().toISOString() });
    console.log('[ProjectCreate] Starting validation');
    
    if (!validateForm()) {
      console.log('[ProjectCreate] Validation failed');
      return;
    }

    console.log('[ProjectCreate] Validation passed');
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      console.log('[ProjectCreate] Preparing request', { hasToken: !!token });
      const requestBody: any = {
        name: formData.name,
        description: formData.description,
        projectNumber: formData.projectNumber.trim().toUpperCase(),
        status: formData.status,
        priority: formData.priority,
        type: formData.type,
        client: { name: formData.clientName.trim() },
        location: formData.location,
        timeline: {
          startDate: formData.startDate,
          endDate: formData.endDate,
        },
        budget: { totalBudget: parseFloat(formData.budget) },
        team: { projectManager: formData.managerId },
      };
      // Admins can explicitly set the company for the project
      if (isAdmin && selectedCompanyId) {
        requestBody.company = selectedCompanyId;
      }
      console.log('[ProjectCreate] Request payload prepared', {
        name: requestBody.name,
        projectNumber: requestBody.projectNumber,
        status: requestBody.status,
        priority: requestBody.priority,
        type: requestBody.type,
        clientName: requestBody.client?.name,
        location: requestBody.location,
        startDate: requestBody.timeline?.startDate,
        endDate: requestBody.timeline?.endDate,
        totalBudget: requestBody.budget?.totalBudget,
        projectManager: requestBody.team?.projectManager,
        company: requestBody.company || null,
      });
      console.log('[ProjectCreate] Dispatching POST', { url: `${API_BASE_URL}/projects` });
      const response = await fetch(`${API_BASE_URL}/projects`, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
      });
      console.log('[ProjectCreate] Response received', { status: response.status });
      if (!response.ok) {
        if (response.status === 403) {
          console.log('[ProjectCreate] Permission error (403)');
          throw new Error("You don't have permission to create projects.");
        }
        const errPayload = await response.json().catch(() => null);
        console.log('[ProjectCreate] Response not OK', { status: response.status, errPayload });
        let msg = errPayload?.message || 'Failed to create project';
        const details = Array.isArray(errPayload?.errors) ? errPayload.errors : [];
        if (details.length) {
          msg = `${msg}: ${details.join('; ')}`;
        }
        throw new Error(msg);
      }
      console.log('[ProjectCreate] Parsing success response');
      const responseJson = await response.json();
      console.log('[ProjectCreate] Project created successfully', { response: responseJson });
      setSuccess(true);
      
      // Redirect to project detail page after a short delay
      setTimeout(() => {
        const createdId = responseJson?.data?._id || responseJson?.data?.id || responseJson?._id || responseJson?.id;
        console.log('[ProjectCreate] Navigating to project details', { projectId: createdId });
        navigate(`/projects/${createdId}`);
      }, 1500);
    } catch (err) {
      console.error('[ProjectCreate] Create Project submit failed', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      console.log('[ProjectCreate] Create Project submit finished');
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/projects');
  };

  // Generate a readable project number and clear validation errors
  const generateProjectNumber = () => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
    const generated = `PRJ-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${rand}`;
    setFormData(prev => ({ ...prev, projectNumber: generated }));
    if (errors.projectNumber) {
      setErrors(prev => ({ ...prev, projectNumber: undefined }));
    }
  };

  // Milestone management functions
  const addMilestone = () => {
    const newMilestone: Milestone = {
      id: Date.now().toString(),
      name: '',
      targetDate: '',
      description: '',
    };
    setFormData({
      ...formData,
      milestones: [...formData.milestones, newMilestone],
    });
  };

  const removeMilestone = (id: string) => {
    setFormData({
      ...formData,
      milestones: formData.milestones.filter(milestone => milestone.id !== id),
    });
  };

  const updateMilestone = (id: string, field: keyof Milestone, value: string) => {
    setFormData({
      ...formData,
      milestones: formData.milestones.map(milestone =>
        milestone.id === id ? { ...milestone, [field]: value } : milestone
      ),
    });
  };

  // Document management functions
  const addDocument = () => {
    const newDocument: ProjectDocument = {
      id: Date.now().toString(),
      name: '',
      file: null,
      description: '',
    };
    setFormData({
      ...formData,
      documents: [...formData.documents, newDocument],
    });
  };

  const removeDocument = (id: string) => {
    setFormData({
      ...formData,
      documents: formData.documents.filter(doc => doc.id !== id),
    });
  };

  const updateDocument = (id: string, field: keyof ProjectDocument, value: string | File) => {
    setFormData({
      ...formData,
      documents: formData.documents.map(doc =>
        doc.id === id ? { ...doc, [field]: value } : doc
      ),
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1">
          Create New Project
        </Typography>
      </Box>

      {/* Success Alert */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Project created successfully! Redirecting to project details...
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Validation Alert */}
      {Object.keys(errors).length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle1">
            {[
              'Please fix the following fields:',
              Object.entries(errors)
                .filter(([_, message]) => !!message)
                .map(([field, message]) => `${field}: ${message}`)
                .join(' • '),
            ].join(' ')}
          </Typography>
        </Alert>
      )}

      {/* Form */}
      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {/* Project Name and Location */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Project Name"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  error={!!errors.name}
                  helperText={errors.name}
                  sx={{ minWidth: 300, flex: 1 }}
                  required
                />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    label="Project Number"
                    value={formData.projectNumber}
                    onChange={handleInputChange('projectNumber')}
                    error={!!errors.projectNumber}
                    helperText={errors.projectNumber}
                    sx={{ minWidth: 200 }}
                    required
                  />
                  <Button variant="outlined" size="small" onClick={generateProjectNumber}>
                    Generate
                  </Button>
                </Box>

                <TextField
                  label="Location"
                  value={formData.location}
                  onChange={handleInputChange('location')}
                  error={!!errors.location}
                  helperText={errors.location}
                  InputProps={{
                    startAdornment: <LocationOn sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                  sx={{ minWidth: 300, flex: 1 }}
                  required
                />
              </Box>

              {/* Description */}
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={4}
                value={formData.description}
                onChange={handleInputChange('description')}
                error={!!errors.description}
                helperText={errors.description}
                required
              />

              {/* Dates and Budget */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={formData.startDate}
                  onChange={handleInputChange('startDate')}
                  error={!!errors.startDate}
                  helperText={errors.startDate}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    startAdornment: <CalendarToday sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                  sx={{ minWidth: 200 }}
                  required
                />

                <TextField
                  label="End Date"
                  type="date"
                  value={formData.endDate}
                  onChange={handleInputChange('endDate')}
                  error={!!errors.endDate}
                  helperText={errors.endDate}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    startAdornment: <CalendarToday sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                  sx={{ minWidth: 200 }}
                  required
                />

                <TextField
                  label="Budget"
                  type="number"
                  value={formData.budget}
                  onChange={handleInputChange('budget')}
                  error={!!errors.budget}
                  helperText={errors.budget}
                  InputProps={{
                    startAdornment: <AttachMoney sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                  sx={{ minWidth: 200 }}
                  required
                />
              </Box>

              {/* Status and Priority */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={handleSelectChange('status')}
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={formData.priority}
                    label="Priority"
                    onChange={handleSelectChange('priority')}
                  >
                    {priorityOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 200 }} error={!!errors.type}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={formData.type}
                    label="Type"
                    onChange={handleSelectChange('type')}
                  >
                    {typeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {!!errors.type && (
                    <FormHelperText>{errors.type}</FormHelperText>
                  )}
                </FormControl>

                {/* Company selector for Admins */}
                {isAdmin && (
                  <FormControl sx={{ minWidth: 250 }}>
                    <InputLabel>Company</InputLabel>
                    <Select
                      value={selectedCompanyId}
                      label="Company"
                      onChange={(e) => setSelectedCompanyId(e.target.value)}
                    >
                      {companies.map((c) => (
                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>

              {/* Client */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Client Name"
                  value={formData.clientName}
                  onChange={handleInputChange('clientName')}
                  error={!!errors.clientName}
                  helperText={errors.clientName}
                  sx={{ minWidth: 300 }}
                  required
                />
              </Box>

              {/* Milestones Section */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Assignment color="primary" />
                    Project Milestones
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={addMilestone}
                  >
                    Add Milestone
                  </Button>
                </Box>
                
                {formData.milestones.map((milestone, index) => (
                  <Card key={milestone.id} sx={{ mb: 2, bgcolor: 'grey.50' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Stack spacing={2}>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                              <TextField
                                label="Milestone Name"
                                value={milestone.name}
                                onChange={(e) => updateMilestone(milestone.id, 'name', e.target.value)}
                                sx={{ minWidth: 250, flex: 1 }}
                                size="small"
                              />
                              <TextField
                                label="Target Date"
                                type="date"
                                value={milestone.targetDate}
                                onChange={(e) => updateMilestone(milestone.id, 'targetDate', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={{ minWidth: 200 }}
                                size="small"
                              />
                            </Box>
                            <TextField
                              label="Description"
                              multiline
                              rows={2}
                              value={milestone.description}
                              onChange={(e) => updateMilestone(milestone.id, 'description', e.target.value)}
                              size="small"
                            />
                          </Stack>
                        </Box>
                        <IconButton
                          onClick={() => removeMilestone(milestone.id)}
                          size="small"
                          sx={{ color: 'black' }}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
                
                {formData.milestones.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No milestones added yet. Click "Add Milestone" to get started.
                  </Typography>
                )}
              </Box>

              {/* Documents Section */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Upload color="primary" />
                    Project Documents
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={addDocument}
                  >
                    Add Document
                  </Button>
                </Box>
                
                {formData.documents.map((document, index) => (
                  <Card key={document.id} sx={{ mb: 2, bgcolor: 'grey.50' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Stack spacing={2}>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                              <TextField
                                label="Document Name"
                                value={document.name}
                                onChange={(e) => updateDocument(document.id, 'name', e.target.value)}
                                sx={{ minWidth: 250, flex: 1 }}
                                size="small"
                              />
                              <Button
                                variant="outlined"
                                component="label"
                                startIcon={<Upload />}
                                sx={{ minWidth: 150 }}
                                size="small"
                              >
                                Choose File
                                <input
                                  type="file"
                                  hidden
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      updateDocument(document.id, 'file', file);
                                      if (!document.name) {
                                        updateDocument(document.id, 'name', file.name);
                                      }
                                    }
                                  }}
                                />
                              </Button>
                            </Box>
                            {document.file && (
                              <Typography variant="body2" color="text.secondary">
                                Selected: {document.file.name} ({(document.file.size / 1024).toFixed(1)} KB)
                              </Typography>
                            )}
                            <TextField
                              label="Description"
                              multiline
                              rows={2}
                              value={document.description}
                              onChange={(e) => updateDocument(document.id, 'description', e.target.value)}
                              size="small"
                            />
                          </Stack>
                        </Box>
                        <IconButton
                          onClick={() => removeDocument(document.id)}
                          size="small"
                          sx={{ color: 'black' }}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
                
                {formData.documents.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No documents added yet. Click "Add Document" to get started.
                  </Typography>
                )}
              </Box>

              {/* Submit Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  onClick={() => {
                    console.log('[ProjectCreate] Create Project button onClick fired');
                  }}
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                >
                  {loading ? 'Creating...' : 'Create Project'}
                </Button>
              </Box>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProjectCreate;